import { Observable } from "rxjs/Observable"

import {
    MODERATION_FEED_LIST_REQUEST,
    MODERATION_FEED_LIST_RESPONSE,
    MODERATION_FEED_LIST_CACHED_RESPONSE,
    MODERATION_FEED_LIST_ERROR_RESPONSE,
    MODERATION_FEED_ITEM_REQUEST,
    MODERATION_FEED_ITEM_RESPONSE,
    MODERATION_FEED_ITEM_CACHED_RESPONSE,
    MODERATION_FEED_ITEM_ERROR_RESPONSE,
    MODERATION_FEED_ITEM_FULLTEXT_REQUEST,
    MODERATION_FEED_ITEM_FULLTEXT_RESPONSE,
    MODERATION_FEED_ITEM_FULLTEXT_CACHED_RESPONSE,
    MODERATION_FEED_ITEM_FULLTEXT_ERROR_RESPONSE,
    MODERATION_FEED_ITEM_SAVE_REQUEST,
    MODERATION_FEED_ITEM_SAVE_RESPONSE,
    MODERATION_FEED_ITEM_SAVE_ERROR_RESPONSE,
    MODERATION_FEED_ANALYSE_REQUEST,
    MODERATION_FEED_ANALYSE_ERROR_RESPONSE
} from "../actions/action-types"
import { mapRequestError } from "./default-epics/epic-utils"
import {
    moderationAnalyseErrorResponse,
    moderationItemResponse,
    moderationItemSaveResponse,
    moderationItemSaveErrorResponse,
    moderationItemCachedResponse,
    moderationItemErrorResponse,
    moderationItemFulltextResponse,
    moderationItemFulltextCachedResponse,
    moderationItemFulltextErrorResponse,
    moderationListCachedResponse,
    moderationListResponse,
    moderationListErrorResponse
} from "../actions/moderation-feed-actions"
import {
    hydrate as hydrateKnowledge,
    normalisingFields
} from "../models/entities/feed"
import { unauthenticated } from "../actions/authentication-actions"
import {
    getModerationList,
    getModerationItem,
    getModerationItemFulltext,
    getModerationItemStatus,
    putModerationItem,
    postModerationItem
} from "../api/moderation-feed-api"
import {
    requestListEpic,
    requestItemEpic,
    requestItemSaveEpic,
    callbackActionCreatorEpic
} from "./default-epics/default-entity-epics"

export const requestModerationListEpic = requestListEpic(
    MODERATION_FEED_LIST_REQUEST,
    getModerationList,
    "moderationFeed",
    moderationListResponse,
    moderationListCachedResponse,
    moderationListErrorResponse
)

export const requestModerationItemEpic = requestItemEpic(
    MODERATION_FEED_ITEM_REQUEST,
    getModerationItem,
    "knowledge",
    hydrateKnowledge,
    normalisingFields,
    moderationItemResponse,
    moderationItemCachedResponse,
    moderationItemErrorResponse
)

export const requestModerationItemFulltextEpic = requestItemEpic(
    MODERATION_FEED_ITEM_FULLTEXT_REQUEST,
    getModerationItemFulltext,
    "knowledgeFulltext",
    hydrateKnowledge,
    normalisingFields,
    moderationItemFulltextResponse,
    moderationItemFulltextCachedResponse,
    moderationItemFulltextErrorResponse
)

export const requestModerationItemSaveEpic = requestItemSaveEpic(
    MODERATION_FEED_ITEM_SAVE_REQUEST,
    putModerationItem,
    () => null,
    "knowledge",
    moderationItemSaveResponse,
    moderationItemSaveErrorResponse
)

// The knowledge scrape is async on the backend: the POST returns a job
// reference immediately, then we poll until scraping completes. See
// dashboard-api/docs/design_poll_sync.md for the contract.
const POLL_INTERVAL_MS = 2000
const MAX_POLL_ATTEMPTS = 45 // ~90s; scrapes typically take ~31s

const pollForCompletion = (token, id, attempt, ctx) => {
    const { dataType, shouldPersist, isSimple, callbackActionCreator } = ctx
    return getModerationItemStatus(token, id).mergeMap(response => {
        if (response.status === "done") {
            // response.item is the v1-identical knowledge item, so downstream
            // (callback epic, reducer, widget) is unchanged.
            return Observable.of(
                moderationItemResponse(
                    response.item,
                    false,
                    callbackActionCreator
                )
            )
        }
        if (response.status === "error" || attempt >= MAX_POLL_ATTEMPTS) {
            const message =
                response.status === "error"
                    ? response.message
                    : "Timed out waiting for knowledge item to be ready"
            return Observable.of(
                moderationAnalyseErrorResponse(
                    new Error(message),
                    dataType,
                    shouldPersist,
                    isSimple,
                    callbackActionCreator
                )
            )
        }
        // pending → wait, then poll again
        return Observable.of(null)
            .delay(POLL_INTERVAL_MS)
            .mergeMap(() => pollForCompletion(token, id, attempt + 1, ctx))
    })
}

export const requestModerationAnalyseEpic = (action$, { getState }) =>
    action$.ofType(MODERATION_FEED_ANALYSE_REQUEST).mergeMap(
        ({ data, dataType, shouldPersist, isSimple, callbackActionCreator }) => {
            const token = getState().authentication.token
            return postModerationItem(
                token,
                data,
                dataType,
                shouldPersist,
                isSimple
            )
                .mergeMap(({ id }) =>
                    pollForCompletion(token, id, 0, {
                        dataType,
                        shouldPersist,
                        isSimple,
                        callbackActionCreator
                    })
                )
                .catch(error =>
                    Observable.of(
                        mapRequestError(
                            error,
                            unauthenticated,
                            moderationAnalyseErrorResponse,
                            dataType,
                            shouldPersist,
                            isSimple,
                            callbackActionCreator
                        )
                    )
                )
        }
    )

export const responseModerationCallbackEpic = callbackActionCreatorEpic(
    MODERATION_FEED_LIST_RESPONSE,
    MODERATION_FEED_LIST_CACHED_RESPONSE,
    MODERATION_FEED_LIST_ERROR_RESPONSE,
    MODERATION_FEED_ITEM_RESPONSE,
    MODERATION_FEED_ITEM_SAVE_RESPONSE,
    MODERATION_FEED_ITEM_CACHED_RESPONSE,
    MODERATION_FEED_ITEM_ERROR_RESPONSE,
    MODERATION_FEED_ITEM_SAVE_ERROR_RESPONSE,
    MODERATION_FEED_ANALYSE_ERROR_RESPONSE,
    MODERATION_FEED_ITEM_FULLTEXT_RESPONSE,
    MODERATION_FEED_ITEM_FULLTEXT_CACHED_RESPONSE,
    MODERATION_FEED_ITEM_FULLTEXT_ERROR_RESPONSE
)
